import { useEffect, useState } from 'react'
import { useSearchUsersQuery } from '@paintscout/api'
import type { User } from '@paintscout/api'
import type { UserAssignments, AppMetadata } from 'paintscout'
import { addAuth0Prefix, removeAuth0Prefix } from '@paintscout/util/users'
import { resolvePermissions } from '@paintscout/util/builder'
import { getDeepObjectDiff } from '@paintscout/util/activity'
import { useSnackbar } from 'notistack'
import { useUser } from '@paintscout/ui-business'
import * as Sentry from '@sentry/react'

const STAFF_ROLES = ['crew', 'sales', 'core', 'team'] as const

function isStaffUser(user: User, currentUserId: string, ownerId?: string): boolean {
  const userId = removeAuth0Prefix(user.user_id)
  if (userId === removeAuth0Prefix(currentUserId)) return false
  if (userId === ownerId) return false
  const roles = user.app_metadata?.roles ?? []
  if (!STAFF_ROLES.some((r) => roles.includes(r))) return false
  const permissions = resolvePermissions(user.app_metadata as AppMetadata)
  return !permissions.includes('read:all-documents')
}

interface Args {
  currentUsers: UserAssignments
  currentCrewIds: string[]
  ownerId?: string
  isSuperadmin: boolean
  clientId: string
}

export function useSelectStaffDialog({ currentUsers, ownerId, isSuperadmin, clientId }: Args) {
  const { user: currentUser } = useUser()
  const { enqueueSnackbar } = useSnackbar()
  const [newAssignedUsers, setNewAssignedUsers] = useState<UserAssignments>(currentUsers)

  const {
    loading: fetching,
    data,
    error
  } = useSearchUsersQuery({
    variables: { query: isSuperadmin ? `app_metadata.companyId:"${clientId}" AND app_metadata.active:true` : '' },
    fetchPolicy: 'network-only'
  })

  useEffect(() => {
    if (error) {
      Sentry.captureException(error)
      enqueueSnackbar('Unable to retrieve Staff list', { variant: 'error' })
    }
  }, [error])

  const assignedUserIds = Object.keys(newAssignedUsers)
    .map((id) => (newAssignedUsers[id].assigned ? id : null))
    .filter(Boolean)

  const users = (data?.searchUsers?.rows ?? []).filter((u) => isStaffUser(u, currentUser.user_id, ownerId))

  const queryLoading = fetching && !data?.searchUsers

  const hasChanges = getDeepObjectDiff(newAssignedUsers, currentUsers).length > 0

  function handleAssignUser(user: User) {
    const userId = removeAuth0Prefix(user.user_id)
    const isSelected = assignedUserIds.indexOf(userId) !== -1
    setNewAssignedUsers((prev) => ({ ...prev, [userId]: { assigned: !isSelected, name: user.name } }))
  }

  return {
    users,
    queryLoading,
    hasChanges,
    assignedUserIds: assignedUserIds.map((id) => addAuth0Prefix(id)),
    newAssignedUsers,
    handleAssignUser
  }
}
